import { Box, Button, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { forwardRef, useImperativeHandle } from 'react';
import { LuKeyRound } from 'react-icons/lu';

import CreateNewConfigModal from '@/components/ui/CreateNewConfigModal/CreateNewConfigModal';
import CreateNewCredentialModal from '@/components/ui/CreateNewCredentialModal/CreateNewCredentialModal';
import SelectWithNewOption from '@/components/ui/SelectWithNewOption/SelectWithNewOption';
import UnsavedChangesModal from '@/components/ui/UnsavedChangesModal/UnsavedChangesModal';
import { TAgent } from '@/types/agents';

import useConfigureStage from './useConfigureStage';

export interface ConfigureStageRef {
  requestLeave: (action: () => void) => void;
}

interface ConfigureStageProps {
  agent: TAgent;
  configId: string;
  onConfigNameChange: (value: string) => void;
  selectedCredentials: Record<string, string>;
  onCredentialChange: (credentialSlotKey: string, value: string) => void;
  setSelectedCredentials: (credentials: Record<string, string>) => void;
  onContinue: () => void;
}

const ConfigureStage = forwardRef<ConfigureStageRef, ConfigureStageProps>(function ConfigureStage(
  {
    agent,
    configId,
    onConfigNameChange,
    selectedCredentials,
    onCredentialChange,
    setSelectedCredentials,
    onContinue,
  },
  ref
) {
  const {
    isModalOpen,
    configOptions,
    isCreatingCredential,
    isCreatingAgentConfig,
    isUpdatingAgentConfig,
    isConfigModalOpen,
    setIsConfigModalOpen,
    handleAddNewCredential,
    handleCloseModal,
    handleSubmitCredential,
    handleCreateCredentialForConfig,
    handleCreateConfig,
    handleUpdateConfig,
    handleContinue,
    handleConfigChange,
    handleDiscardChanges,
    handleStayAndContinueEditing,
    isConfigEdited,
    credentialOptions,
    selectedCredential,
    agentConfigs,
    isUnsavedChangesModalOpen,
    requestLeave,
  } = useConfigureStage({
    agent,
    configId,
    selectedCredentials,
    setSelectedCredentials,
    onConfigNameChange,
    onContinue,
  });

  useImperativeHandle(ref, () => ({ requestLeave }), [requestLeave]);

  const isSaveDisabled = !configId || !isConfigEdited;
  const allCredentialsSelected = agent.credentials.every((_, slotIndex) => {
    const slotKey = `${agent.referenceId}:${slotIndex}`;

    return Boolean(selectedCredentials[slotKey]);
  });
  const isContinueDisabled = !configId || isUpdatingAgentConfig || !allCredentialsSelected;

  return (
    <>
      <VStack align="stretch" gap={6}>
        <Box>
          <Text fontSize={18} fontWeight={500} color="fg" mb={3}>
            Config Name
          </Text>
          <SelectWithNewOption
            options={configOptions}
            value={configId}
            onChange={handleConfigChange}
            placeholder="Enter config name"
            onAddNew={() => setIsConfigModalOpen(true)}
            addNewButtonText="Add new config"
          />
        </Box>

        <Box>
          <Text fontSize={18} fontWeight={500} color="fg" mb={4}>
            Required credentials
          </Text>
          <VStack align="stretch" gap={4}>
            {agent.credentials.map((credential, slotIndex) => {
              const slotKey = `${agent.referenceId}:${slotIndex}`;

              return (
                <Box key={slotKey} pb={4}>
                  <HStack mb={3}>
                    <Icon as={LuKeyRound} color="fg.muted" boxSize={5} />
                    <Text fontSize={16} fontWeight={500} color="fg">
                      {credential.name}
                    </Text>
                  </HStack>
                  <Text fontSize={14} color="fg.muted" mb={3}>
                    Use Credentials
                  </Text>
                  <SelectWithNewOption
                    options={credentialOptions[credential.name] || []}
                    value={selectedCredentials[slotKey]}
                    onChange={(value) => onCredentialChange(slotKey, value)}
                    placeholder="Company API key"
                    onAddNew={() => handleAddNewCredential(credential)}
                    addNewButtonText="Add new credential"
                    isDisabled={!configId}
                  />
                </Box>
              );
            })}
          </VStack>
        </Box>

        <HStack gap={3} width="100%" justify="flex-end" mt={4}>
          <Button
            variant="outline"
            colorPalette="purple"
            borderColor="purple.200"
            color="purple.600"
            borderRadius={6}
            px={4}
            py={2}
            onClick={handleUpdateConfig}
            _hover={{ backgroundColor: 'purple.50' }}
            disabled={isSaveDisabled}
            loading={isUpdatingAgentConfig}
          >
            Save config
          </Button>
          <Button
            variant="solid"
            colorPalette="purple"
            backgroundColor="purple.600"
            color="white"
            borderRadius={6}
            px={4}
            py={2}
            onClick={handleContinue}
            _hover={{ backgroundColor: 'purple.700' }}
            disabled={isContinueDisabled}
          >
            Continue
          </Button>
        </HStack>
      </VStack>

      {selectedCredential && (
        <CreateNewCredentialModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          credential={selectedCredential}
          onSubmit={handleSubmitCredential}
          isLoading={isCreatingCredential}
        />
      )}

      <CreateNewConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        agent={agent}
        agentConfigs={agentConfigs || []}
        credentialOptions={credentialOptions}
        onCreateCredential={handleCreateCredentialForConfig}
        onSubmit={handleCreateConfig}
        isLoading={isCreatingAgentConfig}
        isCreatingCredential={isCreatingCredential}
      />

      <UnsavedChangesModal
        isOpen={isUnsavedChangesModalOpen}
        onClose={handleStayAndContinueEditing}
        onStay={handleStayAndContinueEditing}
        onDiscard={handleDiscardChanges}
      />
    </>
  );
});

export default ConfigureStage;

